import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import {
  Box, Button, Flex, HStack, Skeleton, Spacer, Stack, Switch, Text, useColorModeValue,
} from '@chakra-ui/react';
import {
  FiFolder, FiChevronDown, FiChevronRight, FiInfo,
} from 'react-icons/fi';
import { usePendingRecurringEnrollments } from '../hooks/useRecurringRevenues'; // item 1266
import useCreditMemos from '../hooks/useCreditMemos'; // item 1266 — notice-box smart default
import ExpandCollapseAllButton from '../shared/ExpandCollapseAllButton';
import CollapsibleGroupHeader from '../shared/CollapsibleGroupHeader'; // item 732 — section bucket
import useAutoCollapse from '../hooks/useAutoCollapse';
import useProject from '../hooks/useProject';
import useCurrentUser from '../hooks/useCurrentUser'; // item 734 — role-gate the doc sections
import ProposalsGrid from '../shared/ProposalsGrid';
import ProjectHandOffsSection from '../shared/ProjectHandOffsSection';
import ProjectTasksSection from '../shared/ProjectTasksSection'; // item 1466
import PurchaseOrdersGrid from '../shared/PurchaseOrdersGrid';
import WorkOrdersGrid from '../shared/WorkOrdersGrid';
import InvoiceGrid from '../shared/InvoiceGrid';
import ProjectCreditMemosSection from '../shared/ProjectCreditMemosSection'; // item 1221
import BillGrid from '../shared/BillGrid';
import { ProjectRecurringSection, ProjectPendingEnrollments } from '../shared/RecurringRevenueGrid'; // + item 1266
import ProjectContractsSection from '../shared/ProjectContractsSection'; // item 719
import ArchivedContractsSection from '../shared/ArchivedContractsSection'; // item 1426
import ProjectDocumentsSection from '../shared/ProjectDocumentsSection';
import ProjectLinksCard from '../shared/ProjectLinksCard'; // item 329
import ProjectServerSpecsSection from '../shared/ProjectServerSpecsSection'; // item 1595
import ProjectChatSection from '../shared/ProjectChatSection';
import ProjectHelpDesksSection from '../shared/ProjectHelpDesksSection';
import ProjectCustomerCard from '../shared/ProjectCustomerCard'; // item 485 — CRM on the project

// item 124: the project view's sections are independently collapsible (session-only
// state) with an EXPAND ALL / COLLAPSE ALL toggle, so you can see every category at a
// glance. Default = expanded (current behaviour); Collapse All gives the overview.
type SectionKey = 'projectMgmt' | 'customer' | 'tasks' | 'proposals' | 'workOrders' | 'handOffs' | 'invoices' | 'recurring' | 'contracts' | 'archivedContracts' | 'purchaseOrders' | 'bills' | 'documents' | 'chat' | 'helpDesks' | 'links' | 'serverSpecs';
const SECTIONS: SectionKey[] = ['projectMgmt', 'customer', 'tasks', 'proposals', 'workOrders', 'handOffs', 'invoices', 'recurring', 'contracts', 'archivedContracts', 'purchaseOrders', 'bills', 'documents', 'links', 'serverSpecs', 'helpDesks', 'chat']; // item 1595 serverSpecs · item 1437 — archivedContracts joins the collapse set

const ProjectPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const anchorSx = { scrollMarginTop: '96px' } as const;
  const { data, status, refetch } = useProject({ id });
  // item 734 (Chad's role matrix) — gate the project-view document sections by role, mirroring
  // the sidebar exactly: Team sees Work Orders only (no Proposals/Invoices/Subs/Contracts/POs/
  // Bills — those were "visible under project view today", now resolved to NO); Executive runs
  // ops but sees NO money, so it keeps Proposals/Contracts but not Invoices/Subs/POs/Bills.
  const { isTeam, hasFinancialAccess, isSuperAdmin } = useCurrentUser();
  const showProposals = !isTeam; // Team: no proposals anywhere (Executive keeps them)
  const showMoney = hasFinancialAccess; // hides Invoices/Subscriptions/POs/Bills from Team + Executive
  // item 538 — remember which sections are collapsed so you can focus on one area (e.g.
  // Work Orders) and it stays that way across reloads. Persisted PER PROJECT in
  // localStorage (Chad: each project keeps its own focus layout, not a global one).
  // Defaults to all-expanded; merges over defaults so a newly-added section always starts
  // visible even against an older saved layout.
  const collapseKey = (pid: string) => `basix:projectSections:collapsed:v1:${pid}`;
  const defaultCollapsed = React.useMemo(
    () => SECTIONS.reduce((acc, k) => ({ ...acc, [k]: false }), {} as Record<SectionKey, boolean>),
    [],
  );
  const readCollapsed = React.useCallback((pid: string): Record<SectionKey, boolean> => {
    try {
      const raw = window.localStorage.getItem(collapseKey(pid));
      if (raw) return { ...defaultCollapsed, ...JSON.parse(raw) };
    } catch { /* ignore parse / private-mode errors */ }
    return defaultCollapsed;
  }, [defaultCollapsed]);
  // item 685 (Chad) — personal "Auto-collapse sections" preference (persisted, per browser)
  const [autoCollapse, setAutoCollapse] = useAutoCollapse();
  // item 709 (Chad) — when the preference is on, OPENING a project starts it fully collapsed
  // (a tidy top-of-page overview); otherwise restore the remembered per-section layout.
  const allCollapsedRecord = React.useCallback(
    () => SECTIONS.reduce((acc, k) => ({ ...acc, [k]: true }), {} as Record<SectionKey, boolean>),
    [],
  );
  // item 1207 (Chad: "bug in light mode") — the PM bucket's flat ground was hardcoded
  // dark (732), painting a black slab under light-mode cards; it follows the mode now.
  const pmBucketBg = useColorModeValue('rgba(228, 231, 237, 0.96)', 'rgba(28, 31, 40, 0.96)');
  const [collapsed, setCollapsed] = useState<Record<SectionKey, boolean>>(
    () => (autoCollapse ? allCollapsedRecord() : readCollapsed(id)),
  );
  // switching projects reuses this same component (only the :id param changes), so on id change
  // re-collapse (when the preference is on) or restore the saved layout, and scroll to the top.
  const autoCollapseRef = React.useRef(autoCollapse);
  autoCollapseRef.current = autoCollapse;
  useEffect(() => {
    setCollapsed(autoCollapseRef.current ? allCollapsedRecord() : readCollapsed(id));
    const el = document.getElementById('basix-content-scroll');
    if (el) el.scrollTo({ top: 0 }); else window.scrollTo(0, 0);
  }, [id, readCollapsed, allCollapsedRecord]);
  const persist = (next: Record<SectionKey, boolean>) => {
    try { window.localStorage.setItem(collapseKey(id), JSON.stringify(next)); } catch { /* best-effort */ }
  };

  // item 732 (Chad) — the Project Management bucket + its children. Auto-collapse must NOT treat a
  // child as a peer of the bucket, or opening a sub-section would collapse the bucket and hide it.
  const BUCKET_CHILDREN: SectionKey[] = ['customer', 'tasks', 'handOffs', 'documents', 'links', 'serverSpecs', 'helpDesks', 'chat']; // item 1466 — tasks · item 1595 serverSpecs
  const toggle = (key: SectionKey) => {
    // item 685 (Chad) — when auto-collapse is ON, EXPANDING a section collapses every other section
    // (single-open); collapsing behaves normally. `collapsed[key] === true` means it's currently closed.
    const opening = collapsed[key];
    let next: Record<SectionKey, boolean>;
    if (autoCollapse && opening) {
      next = SECTIONS.reduce((acc, k) => ({ ...acc, [k]: k !== key }), {} as Record<SectionKey, boolean>);
      // item 732 — keep the bucket OPEN when opening one of its children (don't collapse the parent)
      if (BUCKET_CHILDREN.includes(key)) next.projectMgmt = false;
    } else {
      next = { ...collapsed, [key]: !collapsed[key] };
    }
    setCollapsed(next);
    persist(next);
  };
  const setAll = (value: boolean) => {
    const next = SECTIONS.reduce((acc, k) => ({ ...acc, [k]: value }), {} as Record<SectionKey, boolean>);
    setCollapsed(next);
    persist(next);
  };
  const sec = (key: SectionKey) => ({ collapsed: collapsed[key], onToggle: () => toggle(key) });
  // item 553 — the project quick-nav now lives in the context-bar header; a pill click dispatches
  // this event so the target section expands (if collapsed) before the bar scrolls to it.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const key = (e as CustomEvent).detail as SectionKey;
      if (!key) return;
      setCollapsed((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev, [key]: false };
        persist(next);
        return next;
      });
    };
    window.addEventListener('basix:open-section', onOpen);
    return () => window.removeEventListener('basix:open-section', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  // item 1460 — a deep link to a section (`/project/{id}#psec-handOffs`, e.g. from the Sales
  // Bible) expands that section (and the PROJECT MANAGEMENT bucket if it lives inside), then
  // scrolls to it once the page has data. Same expand path as the quick-nav pills.
  useEffect(() => {
    if (status !== 'success') return undefined;
    const key = window.location.hash.replace(/^#psec-/, '') as SectionKey;
    if (!window.location.hash.startsWith('#psec-') || !SECTIONS.includes(key)) return undefined;
    setCollapsed((prev) => {
      const next = { ...prev, [key]: false };
      if (BUCKET_CHILDREN.includes(key)) next.projectMgmt = false;
      persist(next);
      return next;
    });
    const t = window.setTimeout(() => {
      document.getElementById(`psec-${key}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, status]);
  // item 140: one clean toggle — collapse everything to a tidy section-header overview,
  // then expand it all back. Flips label/icon based on the current state.
  const allCollapsed = SECTIONS.every((k) => collapsed[k]);

  // item 1266 — the "outside the Basix Six" notice box's facts (both queries are cached
  // app-wide, so this costs nothing extra). Open credits are remaining > 0 for THIS customer;
  // pending enrollments are this project's sold-not-billing subs.
  const { data: pendingEnrollData } = usePendingRecurringEnrollments();
  const { memos } = useCreditMemos();
  const pendingEnrollCount = (pendingEnrollData?.index ?? [])
    .filter((r) => r.projectId === id && !r.declined).length;
  const openCreditCount = memos
    .filter((m) => m.customerId === data?.customerId && (m.remaining ?? 0) > 0).length;
  const noticeCount = pendingEnrollCount + openCreditCount;
  // null = "no explicit choice yet" → auto-open when something needs eyes, quiet otherwise
  const [noticesOpen, setNoticesOpen] = useState<boolean | null>(null);
  const noticesEffectiveOpen = noticesOpen ?? noticeCount > 0;
  const noticeBorder = useColorModeValue('gray.200', 'whiteAlpha.200');

  return (
    <>
      {/* Show the project as soon as we have data and KEEP it while a save-triggered
          refetch runs in the background — don't drop the whole page back to skeletons
          on every save (Chad). */}
      {data
      && (
        <Stack spacing={2}>
          {/* item 553 — the section quick-nav now lives in the project CONTEXT-BAR header
              (ProjectContextBar); here we just keep the expand/collapse-all control */}
          <Flex align="center">
            <Spacer />
            {/* item 685 (Chad) — personal auto-collapse toggle: open one section, others collapse */}
            <HStack spacing={2} mr={3}>
              <Switch
                size="sm"
                id="project-auto-collapse"
                isChecked={autoCollapse}
                onChange={(e) => setAutoCollapse(e.target.checked)}
              />
              <Text as="label" htmlFor="project-auto-collapse" fontSize="xs" cursor="pointer">
                Auto-collapse sections
              </Text>
            </HStack>
            <ExpandCollapseAllButton allOpen={!allCollapsed} onToggle={() => setAll(!allCollapsed)} />
          </Flex>
          {/* item 1266 (Chad: "the credits and pending enroll feel out of place — a collapsible
              info box at the top for things that are not part of the Basix Six") — the notice
              box: customer credits + sold-but-not-enrolled subscriptions, the in-between things.
              Auto-opens when something needs eyes; collapses to one quiet line otherwise. The
              mechanics are untouched — credits still apply at invoice finalize, Enroll still
              opens the same wizard. */}
          {showMoney && (
            <Box
              borderWidth="1px"
              borderColor={noticeCount > 0 ? 'orange.400' : noticeBorder}
              borderRadius="lg"
              px={{ base: 3, md: 4 }}
              py={2}
            >
              <HStack
                spacing={2}
                cursor="pointer"
                onClick={() => setNoticesOpen(!noticesEffectiveOpen)}
                userSelect="none"
              >
                <Box as={FiInfo} color={noticeCount > 0 ? 'orange.400' : undefined} />
                <Text fontSize="sm" fontWeight="bold" letterSpacing="0.06em">
                  PROJECT PRIMITIVES
                </Text>
                <Text fontSize="xs" opacity={0.65}>
                  {noticeCount > 0
                    ? `${pendingEnrollCount > 0 ? `${pendingEnrollCount} subscription${pendingEnrollCount === 1 ? '' : 's'} to enroll` : ''}${pendingEnrollCount > 0 && openCreditCount > 0 ? ' · ' : ''}${openCreditCount > 0 ? `${openCreditCount} open credit${openCreditCount === 1 ? '' : 's'}` : ''}`
                    : 'Credits & pending enrollments — nothing needs you right now'}
                </Text>
                <Spacer />
                <Box as={noticesEffectiveOpen ? FiChevronDown : FiChevronRight} />
              </HStack>
              {noticesEffectiveOpen && (
                <Box mt={2}>
                  <ProjectPendingEnrollments projectId={id} />
                  <ProjectCreditMemosSection projectId={id} customerId={data?.customerId} />
                </Box>
              )}
            </Box>
          )}
          {/* the financial workflow: Proposal → Work Order → Invoice (+ subscriptions / contracts /
              purchasing) leads the page */}
          {showProposals && (
          <Box id="psec-proposals" sx={anchorSx}>
            <ProposalsGrid status={status} data={data.proposals} projectId={id} collapse={sec('proposals')} />
          </Box>
          )}
          <Box id="psec-workOrders" sx={anchorSx}>
            <WorkOrdersGrid status={status} data={data.workOrders} projectId={id} collapse={sec('workOrders')} proposals={data.proposals} />
          </Box>
          {showMoney && (
          <Box id="psec-invoices" sx={anchorSx}>
            <InvoiceGrid status={status} data={data.invoices} projectId={id} collapse={sec('invoices')} />
            {/* item 1221 credits moved to the top notice box (item 1266) */}
          </Box>
          )}
          {showMoney && (
          <Box id="psec-recurring" sx={anchorSx}>
            <ProjectRecurringSection projectId={id} collapse={sec('recurring')} />
          </Box>
          )}
          {showProposals && (
          <Box id="psec-contracts" sx={anchorSx}>
            <ProjectContractsSection
              projectId={id}
              proposals={data.proposals}
              collapse={sec('contracts')}
              customerType={data.customer?.customerType} // item 1386 — End User → direct-channel paper
            />
          </Box>
          )}
          {showProposals && (
          <Box id="psec-archivedContracts" sx={anchorSx}>
            {/* item 1426 — archived papers get their own area (renders nothing when there are none).
                item 1437 — a COLLAPSIBLE section like its neighbours: the standalone shell reserves
                ~100vh (item 1063), which opened a huge gap between CONTRACTS and PURCHASE ORDERS. */}
            <ArchivedContractsSection projectId={id} collapse={sec('archivedContracts')} />
          </Box>
          )}
          {showMoney && (
          <Box id="psec-purchaseOrders" sx={anchorSx}>
            <PurchaseOrdersGrid status={status} data={data.purchaseOrders} projectId={id} collapse={sec('purchaseOrders')} />
          </Box>
          )}
          {showMoney && (
          <Box id="psec-bills" sx={anchorSx}>
            <BillGrid status={status} data={data.bills} projectId={id} collapse={sec('bills')} />
          </Box>
          )}

          {/* item 732 (Chad) — PROJECT MANAGEMENT bucket at the BOTTOM: customer + hand-offs +
              documents + links + help desks. The whole bucket sits on ONE flat GRAY ground (the
              aurora glow — an aria-hidden <div>, not the svg icons — is suppressed inside so every
              row matches), delineating it from the aurora top-level sections. Conditionally rendered
              (not a Chakra Collapse) so sub-sections expand freely. */}
          <Box
            mx={{ base: -4, md: -10 }}
            px={{ base: 4, md: 10 }}
            bg={pmBucketBg}
            sx={{ '& div[aria-hidden="true"]': { display: 'none' } }}
          >
            <Box id="psec-projectMgmt" sx={anchorSx}>
              <CollapsibleGroupHeader
                title="Project Management"
                icon={<FiFolder />}
                collapse={sec('projectMgmt')}
                subtitle="Customer · tasks · hand-offs · documents · links · help desks · project talk"
              />
            </Box>
            {!collapsed.projectMgmt && (
              <Stack spacing={2}>
                {/* item 485 — the customer's CRM info + staff, read-only */}
                <Box id="psec-customer" sx={anchorSx}>
                  <ProjectCustomerCard customer={(data as any).customer} collapse={sec('customer')} />
                </Box>
                {/* item 1466 — the project's TASKS (same rows as Tools › Department Tasks) */}
                <Box id="psec-tasks" sx={anchorSx}>
                  <ProjectTasksSection projectId={id} projectName={data.name} customerName={data.customerName} collapse={sec('tasks')} />
                </Box>
                {/* item 461/553 — hand-offs as a standard collapsible ledger section */}
                <Box id="psec-handOffs" sx={anchorSx}>
                  <ProjectHandOffsSection proposals={data.proposals} collapse={sec('handOffs')} />
                </Box>
                <Box id="psec-documents" sx={anchorSx}>
                  <ProjectDocumentsSection projectId={id} collapse={sec('documents')} />
                </Box>
                {/* item 329 — named URLs live next to the uploaded documents */}
                <Box id="psec-links" sx={anchorSx}>
                  <ProjectLinksCard projectId={id} collapse={sec('links')} />
                </Box>
                {/* item 1595 — Tier 3 server specs (vendor-neutral RFQ PDF, no money). Pulse
                    super-admin only during rollout; the API enforces the same gate. */}
                {isSuperAdmin && (
                <Box id="psec-serverSpecs" sx={anchorSx}>
                  <ProjectServerSpecsSection projectId={id} collapse={sec('serverSpecs')} />
                </Box>
                )}
                {/* item 374 — Slack-bridged support threads */}
                <Box id="psec-helpDesks" sx={anchorSx}>
                  <ProjectHelpDesksSection projectId={id} collapse={sec('helpDesks')} />
                </Box>
                {/* item 739 (Chad) — Project Talk moved into the PM bucket */}
                <Box id="psec-chat" sx={anchorSx}>
                  <ProjectChatSection projectId={id} collapse={sec('chat')} />
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      )}
      {/* first load only (no data yet) — a background refetch never lands here */}
      {(status === 'loading' && !data)
      && (
        <Stack>
          <Skeleton key={1} height="50px" />
          <Skeleton key={2} height="25px" />
          <Skeleton key={3} height="25px" />
          <br />
          <Skeleton key={4} height="50px" />
          <Skeleton key={5} height="25px" />
          <Skeleton key={6} height="25px" />
          <br />
          <Skeleton key={7} height="50px" />
          <Skeleton key={8} height="25px" />
          <Skeleton key={9} height="25px" />
        </Stack>
      )}
      {/* a failed fetch with nothing cached used to strand the page on skeletons
          forever (react-query won't auto-retry — refetchOnWindowFocus is off). Offer a
          Retry instead so a transient API blip can't wedge the whole project. */}
      {(status === 'error' && !data)
      && (
        <Stack spacing={4} align="center" py={16}>
          <Text fontWeight="semibold">This project couldn’t be loaded.</Text>
          <Text fontSize="sm" color="gray.500">The server may have been briefly unavailable.</Text>
          <Button onClick={() => refetch()}>Retry</Button>
        </Stack>
      )}
    </>
  );
};
export default withAuthenticationRequired(ProjectPage);
